import { useEffect, useRef, type ReactNode } from 'react';
import { projectTimeline } from '@lw/shared/time-sync';
import type { ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import { fmtTime } from '../app/helpers.js';
import { PeerChip, SyncChip } from './Chips.js';

/**
 * §11.3 CountdownPanel: bottom-up vertical bar, notch at (F−L)/F, BigTimer ceil((next − serverNow)/1000),
 * all written imperatively per rAF frame from serverNow() with a 250 ms fallback for throttled tabs.
 * No decrementing counter exists anywhere. Digits stay visible from the wall anchor in DEGRADED mode.
 */
export function CountdownPanel({ snapshot, provisional }: { snapshot: ClientSnapshot; provisional?: boolean }): ReactNode {
  const { rt } = useRuntime();
  const tl = snapshot.timeline;
  const digits = useRef<HTMLDivElement>(null);
  const lbl = useRef<HTMLDivElement>(null);
  const fillA = useRef<HTMLDivElement>(null);
  const fillR = useRef<HTMLDivElement>(null);
  const marker = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tl) return;
    let raf = 0;
    let last = 0;
    const paint = (): void => {
      const now = rt.client.chrono.serverNow();
      const d = digits.current;
      const l = lbl.current;
      if (!d || !l) return;
      if (now === null) {
        d.textContent = '—';
        d.className = 'digits amber';
        return;
      }
      const p = projectTimeline(tl, now);
      const next = p.phase === 'COUNTDOWN' ? 'Laser on in' : p.phase === 'LASER_ON_PHASE' ? 'Impact in' : 'Impact';
      if (l.textContent !== next) l.textContent = next;
      const text = p.phase === 'IMPACT_PHASE' || p.phase === 'AAR_DUE' ? '0' : String(p.digitsS);
      if (d.textContent !== text) d.textContent = text;
      const cls = `digits ${p.laserWarn || p.impactWarn ? 'red' : p.phase === 'COUNTDOWN' ? 'amber' : 'red'}`;
      if (d.className !== cls) d.className = cls;
      const pct = `${(p.barFraction * 100).toFixed(2)}%`;
      if (fillA.current) fillA.current.style.height = `${(Math.min(p.barFraction, p.notch) * 100).toFixed(2)}%`;
      if (fillR.current) fillR.current.style.height = `${(Math.max(0, p.barFraction - p.notch) * 100).toFixed(2)}%`;
      if (marker.current) marker.current.style.bottom = pct;
      const ladder = document.getElementById('ladderFill');
      if (ladder) ladder.style.width = pct;
    };
    const loop = (t: number): void => {
      if (t - last >= 16) {
        last = t;
        paint();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const fallback = setInterval(paint, 250);
    const vis = (): void => paint();
    document.addEventListener('visibilitychange', vis);
    paint();
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(fallback);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [tl, rt]);
  if (!tl) return null;
  const notch = ((tl.F - tl.L) / tl.F) * 100;
  const role = rt.id.role;
  const mode = rt.client.chrono.currentMode();
  const lateBy = tl.t0Meta.flags.includes('LATE') ? snapshot.timeline && rt.client.events.find((e) => e.type === 'FIRED')?.delayMs : 0;
  return (
    <div className="countdown" aria-live="off" data-testid="countdown">
      <div className="vbar">
        <div className="cap top">Impact</div>
        <div className="track">
          <div className="fill-amber" ref={fillA} />
          <div className="fill-red" ref={fillR} style={{ bottom: `${notch}%` }} />
        </div>
        <div className="notch" style={{ bottom: `${notch}%` }} />
        <div className="notch-lbl" style={{ bottom: `${notch}%` }}>Laser on</div>
        <div className="marker" ref={marker} />
        <div className="cap bottom">Fire</div>
      </div>
      <div className="timer">
        <div className="lbl" ref={lbl}>Laser on in</div>
        <div className="digits amber" ref={digits} data-testid="digits">—</div>
        <div className="sub">
          Shot <b>{fmtTime(tl.t0, true)}</b> · laser on <b>{fmtTime(tl.tLaserOn, true)}</b> · impact <b>{fmtTime(tl.tImpact, true)}</b>
          {lateBy && lateBy > 2000 ? <> · <span className="amber">arrived {(lateBy / 1000).toFixed(1)} s late</span></> : null}
        </div>
        <div className="status-line">
          <SyncChip />
          {role !== 'OBSERVER' ? <PeerChip /> : null}
          {role !== 'OBSERVER' ? snapshot.uav.laserOnConfirmedAt ? <span className="chip ok">Lasing</span> : <LasingUnconfirmed tl={tl} /> : null}
          {provisional ? <span className="chip warn" data-testid="unconfirmed">Fire unconfirmed — sending</span> : null}
          {mode === 'DEGRADED_WALL' ? <span className="chip warn">Degraded — wall clock</span> : null}
        </div>
      </div>
    </div>
  );
}

function LasingUnconfirmed({ tl }: { tl: NonNullable<ClientSnapshot['timeline']> }): ReactNode {
  const { rt } = useRuntime();
  const now = rt.client.chrono.serverNow();
  if (now === null || now < tl.tLaserOn) return null;
  return <span className="chip warn">Lasing — unconfirmed</span>;
}
